import React from 'react';
import styled, { css } from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { hiddenRowCount, IListViewMore, inlineRowsFor, ListViewMoreFooter } from '../ListViewMore';
import { panelHeading } from '../panelHeading';

export type DriverListTone = 'positive' | 'negative';

export interface IDriverListItem {
  rank: number;
  label: string;
  subLabel?: string;
  /** Pre-formatted by the caller — the impact unit differs per card (`+11`, `4.2 pp`, `$800`). */
  value: string;
}

export interface IDriverListProps {
  tone: DriverListTone;
  /** Rendered uppercase by the label type role; pass the copy in its natural case. */
  title: string;
  items: IDriverListItem[];
  /**
   * Shown in place of the list when the set is empty, per `EventTimeline`. Passing it is what keeps
   * the titled box on screen for an empty set; omitted, the component draws nothing at all, so every
   * Layer 1 caller renders exactly as before. PLA-8828.
   */
  emptyText?: string;
  /**
   * Slot for the trailing affordance. Only Customer 360's Risk & Evidence blockers panel draws one
   * (3583-9491); every Layer 1 caller omits it and renders exactly as before. PLA-8828.
   */
  footer?: React.ReactNode;
  /**
   * Caps the rail at six drivers and moves the rest behind a `View more` overlay. Ignored when the
   * caller supplies its own `footer`, so Customer 360's hand-wired link keeps precedence over the
   * shared one rather than the box drawing two. PLA-8844.
   */
  viewMore?: IListViewMore;
  className?: string;
}

const TONE_BACKGROUNDS: Record<DriverListTone, string> = {
  positive: tokens.color['tint-positive-bg'],
  negative: tokens.color['tint-negative-bg']
};

const TONE_HUES: Record<DriverListTone, string> = {
  positive: tokens.color.positive,
  negative: tokens.color.negative
};

const BORDER_OPACITY = 0.3;

/* The library box outlines its tint at 30%. A token is a CSS custom-property reference, so the
   alpha cannot be folded into the value and color-mix() is outside this repo's browser floor:
   the border is painted as its own translucent layer, per ConfidenceBadge. */
const tintedOutline = css<{ $tone: DriverListTone }>`
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 1px solid ${({ $tone }) => TONE_HUES[$tone]};
    border-radius: inherit;
    opacity: ${BORDER_OPACITY};
    pointer-events: none;
  }
`;

const DriverBox = styled.section<{ $tone: DriverListTone; $perx3: boolean }>`
  position: relative;
  padding: ${tokens.space['12']};
  border-radius: ${tokens.radius.md};
  background: ${({ $tone }) => TONE_BACKGROUNDS[$tone]};

  /* PERX3-REDESIGN: the tint alone carries the valence, so the outline is not drawn. The layer was
     an overlay rather than a box border, so dropping it moves nothing — the padding, the radius and
     the fill are untouched. Off the flag the outline is emitted exactly as before. */
  ${({ $perx3 }) => ($perx3 ? null : tintedOutline)}
`;

const Title = styled.h4`
  ${panelHeading}
`;

const ItemList = styled.ol`
  margin: ${tokens.space['8']} 0 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
`;

const RankBadge = styled.span<{ $tone: DriverListTone }>`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-right: ${tokens.space['8']};
  border-radius: ${tokens.radius.sm};
  background: ${({ $tone }) => TONE_HUES[$tone]};
  color: ${tokens.color['on-accent']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: 16px;
  text-align: center;
`;

const ItemText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ItemLabel = styled.span`
  display: block;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const ItemSubLabel = styled.span`
  display: block;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const ItemValue = styled.span`
  flex: 0 0 auto;
  margin-left: ${tokens.space['8']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
  white-space: nowrap;
`;

const EmptyText = styled.p`
  margin: ${tokens.space['8']} 0 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${tokens.space['8']};
`;

const DriverList: React.FC<IDriverListProps> = ({
  tone,
  title,
  items,
  emptyText,
  footer,
  viewMore,
  className
}) => {
  const perx3 = usePerx3Redesign();
  const drivers = Array.isArray(items) ? items : [];
  const capped = footer ? undefined : viewMore;

  // The FR forbids fabricating drivers, so an empty set has nothing to rank; a caller that wants the
  // titled box held open for the real data says so with `emptyText`.
  if (drivers.length === 0 && !emptyText) {
    return null;
  }

  return (
    <DriverBox
      className={className}
      $tone={tone}
      $perx3={perx3}
      data-testid="driver-list"
      data-tone={tone}
    >
      <Title data-testid="driver-list-title">{title}</Title>
      {drivers.length === 0 ? (
        <EmptyText data-testid="driver-list-empty">{emptyText}</EmptyText>
      ) : (
        <ItemList>
          {inlineRowsFor(drivers, capped).map(driver => (
            <Item key={`${driver.rank}-${driver.label}`} data-testid="driver-list-item">
              <RankBadge $tone={tone} aria-hidden="true" data-testid="driver-list-rank">
                {driver.rank}
              </RankBadge>
              <ItemText>
                <ItemLabel data-testid="driver-list-label">{driver.label}</ItemLabel>
                {driver.subLabel ? (
                  <ItemSubLabel data-testid="driver-list-sub-label">{driver.subLabel}</ItemSubLabel>
                ) : null}
              </ItemText>
              <ItemValue data-testid="driver-list-value">{driver.value}</ItemValue>
            </Item>
          ))}
        </ItemList>
      )}
      {footer ? <Footer>{footer}</Footer> : null}
      {hiddenRowCount(drivers, capped) > 0 ? (
        <ListViewMoreFooter
          rows={drivers}
          viewMore={capped}
          // One renderer, called twice: the overlay draws the SAME rail, uncapped.
          renderFull={() => <DriverList tone={tone} title={title} items={drivers} />}
        />
      ) : null}
    </DriverBox>
  );
};

export default DriverList;
